import { PlayerRef } from "@remotion/player";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTimelineContext } from "../../context/use-timeline-context";
import { useMediaQuery } from "react-responsive";
import { MOBILE_VIEW } from "../../../../../../../variables";
import DesktopView from "./desktop-view";
import MobileView from "./mobile-view";

const formatTime = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
};

export const ActionRow: React.FC<{
  playerRef: React.RefObject<PlayerRef | null>;
}> = ({ playerRef }) => {
  const { durationInFrames, fps } = useTimelineContext();
  const [isPlaying, setIsPlaying] = useState(false);
  const isMobile = useMediaQuery({ query: `(max-width: ${MOBILE_VIEW}px)` });

  const currentTimeRef = useRef(0);

  useEffect(() => {
    if (!playerRef.current) return;

    currentTimeRef.current = playerRef.current.getCurrentFrame() / fps;

    const handleTimeUpdate = () => {
      if (playerRef.current) {
        currentTimeRef.current = playerRef.current.getCurrentFrame() / fps;
      }
    };

    playerRef.current.addEventListener("timeupdate", handleTimeUpdate);
    playerRef.current.addEventListener("seeked", handleTimeUpdate);

    return () => {
      if (playerRef.current) {
        playerRef.current.removeEventListener("timeupdate", handleTimeUpdate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        playerRef.current.removeEventListener("seeked", handleTimeUpdate);
      }
    };
  }, [playerRef, fps]);

  const togglePlayPause = useCallback(() => {
    if (!playerRef.current) return;
    if (playerRef.current.isPlaying()) {
      playerRef.current.pause();
    } else {
      playerRef.current.play();
    }
  }, [playerRef]);

  const getCurrentTimeInSeconds = useCallback(() => {
    if (playerRef.current) {
      currentTimeRef.current = playerRef.current.getCurrentFrame() / fps;
    }
    return currentTimeRef.current;
  }, [playerRef, fps]);

  const getTotalDurationInSeconds = useCallback(() => {
    return durationInFrames / fps;
  }, [durationInFrames, fps]);

  // Add effect to update the isPlaying state
  useEffect(() => {
    if (!playerRef.current) return;

    // Set initial state
    setIsPlaying(playerRef.current.isPlaying());

    // Define event handlers
    const handlePlayStateChange = () => {
      if (playerRef.current) {
        setIsPlaying(playerRef.current.isPlaying());
      }
    };

    // Add event listeners
    playerRef.current.addEventListener("play", handlePlayStateChange);
    playerRef.current.addEventListener("pause", handlePlayStateChange);

    // Clean up
    return () => {
      if (playerRef.current) {
        playerRef.current.removeEventListener("play", handlePlayStateChange);
        playerRef.current.removeEventListener("pause", handlePlayStateChange);
      }
    };
  }, [playerRef]);

  return isMobile ? (
    <MobileView
      formatTime={formatTime}
      getTotalDurationInSeconds={getTotalDurationInSeconds}
      getCurrentTimeInSeconds={getCurrentTimeInSeconds}
      togglePlayPause={togglePlayPause}
      isPlaying={isPlaying}
    />
  ) : (
    <DesktopView
      formatTime={formatTime}
      playerRef={playerRef}
      getTotalDurationInSeconds={getTotalDurationInSeconds}
      getCurrentTimeInSeconds={getCurrentTimeInSeconds}
      togglePlayPause={togglePlayPause}
      isPlaying={isPlaying}
    />
  );
};
